import { Button, ButtonGroup, Field, Label, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, SectionGroup } from 'Components/Layout';
import { IWithWorksheetInjectedProps } from 'Components/Worksheets//Core';
import { ConeType, CorrelationFactorSource } from 'Components/Worksheets/AtterbergLimitWorksheet/types';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { FormatTypes } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

let BsEnIsoLiquidLimitCorrelationFactorSection: React.FC<IWithWorksheetInjectedProps> = ({
  namePrefix,
  createWorksheetSelector,
}) => {
  const dispatch = useDispatch();

  const manualSetOfCorrelationFactorParameters = useSelector(
    pipe(
      createWorksheetSelector!('CorrelationFactorSource'),
      (x) => x === CorrelationFactorSource.CalculatedFromEnteredEquation
    )
  );
  const manualCorrelationFactorValue = useSelector(
    pipe(createWorksheetSelector!('CorrelationFactorSource'), (x) => x === CorrelationFactorSource.EnteredValue)
  );

  const coneType = useSelector(createWorksheetSelector!('ConeType'));

  const correlationFactorParameterA = useSelector(createWorksheetSelector!('CorrelationFactorParameterA'));
  const correlationFactorParameterB = useSelector(createWorksheetSelector!('CorrelationFactorParameterB'));
  const correlationFactorParameterC = useSelector(createWorksheetSelector!('CorrelationFactorParameterC'));
  const correlationFactorParameterD = useSelector(createWorksheetSelector!('CorrelationFactorParameterD'));

  const [parameterA, setParameterA] = useState(correlationFactorParameterA);
  const [parameterB, setParameterB] = useState(correlationFactorParameterB);
  const [parameterC, setParameterC] = useState(correlationFactorParameterC);
  const [parameterD, setParameterD] = useState(correlationFactorParameterD);

  useEffect(() => {
    if (!manualSetOfCorrelationFactorParameters) {
      setParameterA('');
      setParameterB('');
      setParameterC('');
      setParameterD('');
    }
  }, [manualSetOfCorrelationFactorParameters]);

  var correlationFactorSourceOptions = [
    {
      label: 'Calculated from BS 1377 equations',
      value: CorrelationFactorSource.CalculatedFromBS1377Equations,
      disabled: coneType === ConeType.Type_60_60,
    },
    {
      label: 'Calculated from entered equation constants',
      value: CorrelationFactorSource.CalculatedFromEnteredEquation,
      disabled: coneType === ConeType.Type_60_60,
    },
    { label: 'Entered Value', value: CorrelationFactorSource.EnteredValue },
  ];

  const recalculateCorrelationFactorValue = () => {
    dispatch(
      actions.patchValues({
        name: namePrefix!,
        keyValuePairs: [
          {
            key: 'CorrelationFactorParameterA',
            value: parameterA,
          },
          {
            key: 'CorrelationFactorParameterB',
            value: parameterB,
          },
          {
            key: 'CorrelationFactorParameterC',
            value: parameterC,
          },
          {
            key: 'CorrelationFactorParameterD',
            value: parameterD,
          },
        ],
        sync: true,
      })
    );
  };

  const config = {
    loader: { load: ['input/asciimath'] },
    asciimath: {
      displaystyle: true,
      delimiters: [
        ['$', '$'],
        ['`', '`'],
      ],
    },
  };

  return (
    <SectionGroup heading="Correlation Factor">
      <ListFieldSection single>
        <Field input={<RadioGroup />} name="CorrelationFactorSource" options={correlationFactorSourceOptions} />
      </ListFieldSection>
      <ListFieldSection single visible={manualSetOfCorrelationFactorParameters && coneType === ConeType.Type_80_30}>
        <FieldSet>
          <Label>Correlation Factor Equation Format</Label>
          <MathJaxContext config={config}>
            <MathJax inline>{'$CFV = a * P^{3/2} + b * P + c * P^{1/2} + d$'}</MathJax>
          </MathJaxContext>
        </FieldSet>
      </ListFieldSection>
      <ListFieldSection visible={manualSetOfCorrelationFactorParameters && coneType === ConeType.Type_80_30}>
        <Field
          input={<NumericInput />}
          noRecase
          onBlur={(e: any) => setParameterA(e.target.value)}
          value={parameterA}
          label="a"
          format={{ type: FormatTypes.SignificantFigures, precision: 10 }}
          sfPrecision={10}
        />
        <Field
          input={<NumericInput />}
          onBlur={(e: any) => setParameterB(e.target.value)}
          noRecase
          value={parameterB}
          label="b"
          format={{ type: FormatTypes.SignificantFigures, precision: 10 }}
          sfPrecision={10}
        />
        <Field
          input={<NumericInput />}
          onBlur={(e: any) => setParameterC(e.target.value)}
          noRecase
          value={parameterC}
          label="c"
          format={{ type: FormatTypes.SignificantFigures, precision: 10 }}
          sfPrecision={10}
        />
        <Field
          input={<NumericInput />}
          onBlur={(e: any) => setParameterD(e.target.value)}
          noRecase
          value={parameterD}
          label="d"
          format={{ type: FormatTypes.SignificantFigures, precision: 10 }}
          sfPrecision={10}
        />
        <ButtonGroup>
          <Button onClick={recalculateCorrelationFactorValue}>Calculate</Button>
        </ButtonGroup>
      </ListFieldSection>
      <ListFieldSection>
        <Field input={manualCorrelationFactorValue ? <NumericInput /> : <TextOutput />} name="CorrelationFactor" />
      </ListFieldSection>
    </SectionGroup>
  );
};

BsEnIsoLiquidLimitCorrelationFactorSection = withInjectedProps(BsEnIsoLiquidLimitCorrelationFactorSection);

export { BsEnIsoLiquidLimitCorrelationFactorSection };
export default BsEnIsoLiquidLimitCorrelationFactorSection;
